<template>
  <v-card class="pph-card" elevation="4" dark>
    <v-card-title class="py-2">
      <h2 class="section-title">
        <v-icon left small>mdi-speedometer</v-icon>
        PPH Monitor
      </h2>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <v-chip color="success" class="mr-2" small>
          <span class="font-weight-bold">{{ stats.current }}</span>
          <span class="ml-1">aktuell</span>
        </v-chip>
        <v-chip color="info" class="mr-2" small>
          <span class="font-weight-bold">{{ Math.round(stats.average) }}</span>
          <span class="ml-1">Ø</span>
        </v-chip>
        <v-chip color="warning" class="mr-2" small>
          <span class="font-weight-bold">{{ stats.high }}</span>
          <span class="ml-1">max</span>
        </v-chip>
        <v-chip color="error" small>
          <span class="font-weight-bold">{{ stats.low }}</span>
          <span class="ml-1">min</span>
        </v-chip>
      </div>
    </v-card-title>
    <v-card-text class="pa-2">
      <v-row>
        <!-- Speedometer Gauge -->
        <v-col cols="12" md="4" class="d-flex align-center justify-center">
          <div id="speedometer-container" style="width: 100%; max-width: 200px;">
            <apexchart
              type="radialBar"
              :height="200"
              :options="speedometerOptions"
              :series="speedometerSeries"
            ></apexchart>
            <div class="text-center mt-n4">
              <div class="caption grey--text">Pakete/Stunde</div>
            </div>
          </div>
        </v-col>
        
        <!-- PPH Chart -->
        <v-col cols="12" md="8">
          <div id="pph-chart-container" style="height: 180px;">
            <apexchart 
              v-if="chartSeries[0].data.length > 0"
              type="area" 
              height="180" 
              :options="chartOptions" 
              :series="chartSeries"
            ></apexchart>
            <div v-else class="text-center py-3">
              <v-icon size="32" color="grey">mdi-chart-line</v-icon>
              <div class="caption grey--text">Lade PPH Daten...</div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'PPHMonitor',
  
  components: {
    apexchart: VueApexCharts,
  },
  
  props: {
    stats: {
      type: Object,
      default: () => ({
        current: 0,
        average: 0,
        high: 0,
        low: 0
      })
    },
    history: {
      type: Array,
      default: () => []
    }
  },
  
  mounted() {
    console.log('[PPHMonitor] Component mounted with stats:', this.stats, 'history:', this.history);
    if (this.history && this.history.length > 0) {
      this.updateChart(this.history);
    }
    if (this.stats) {
      this.updateSpeedometer(this.stats);
    }
  },
  
  data() {
    return {
      speedometerOptions: {
        chart: {
          type: 'radialBar',
          offsetY: -20,
          background: 'transparent'
        },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 135,
            hollow: {
              margin: 0,
              size: '70%',
              background: 'transparent',
              image: undefined,
              imageOffsetX: 0,
              imageOffsetY: 0,
              position: 'front',
              dropShadow: {
                enabled: true,
                top: 3,
                left: 0,
                blur: 4,
                opacity: 0.24
              }
            },
            track: {
              background: 'rgba(255, 255, 255, 0.1)',
              strokeWidth: '100%',
              margin: 0,
              dropShadow: {
                enabled: true,
                top: -3,
                left: 0,
                blur: 4,
                opacity: 0.35
              }
            },
            dataLabels: {
              show: true,
              name: {
                offsetY: -10,
                show: true,
                color: '#888',
                fontSize: '14px'
              },
              value: {
                formatter: function(val) {
                  return 0; // Will be updated dynamically
                },
                color: '#fff',
                fontSize: '36px',
                fontWeight: 'bold',
                show: true,
                offsetY: 5
              }
            }
          }
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            type: 'horizontal',
            shadeIntensity: 0.5,
            gradientToColors: ['#FF4560', '#FEB019', '#00E396'],
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 50, 100]
          }
        },
        stroke: {
          lineCap: 'round'
        },
        labels: ['PPH'],
        colors: ['#00E396'],
        theme: {
          mode: 'dark'
        }
      },
      speedometerSeries: [0],
      chartOptions: {
        chart: {
          id: 'pph-chart',
          type: 'area',
          toolbar: { show: false },
          background: 'transparent',
          defaultLocale: 'de',
          locales: [{
            name: 'de',
            options: {
              months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
              shortMonths: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
              days: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
              shortDays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
              toolbar: {
                exportToSVG: 'Als SVG herunterladen',
                exportToPNG: 'Als PNG herunterladen',
                menu: 'Menü',
                selection: 'Auswahl',
                selectionZoom: 'Auswahl Zoom',
                zoomIn: 'Hineinzoomen',
                zoomOut: 'Herauszoomen',
                pan: 'Verschieben',
                reset: 'Zoom zurücksetzen'
              }
            }
          }]
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.2
          }
        },
        xaxis: {
          type: 'datetime',
          labels: {
            style: { colors: '#9e9e9e' },
            datetimeUTC: false,  // Use local timezone
            datetimeFormatter: {
              hour: 'HH:mm'
            }
          }
        },
        yaxis: {
          title: {
            text: 'PPH',
            style: { color: '#9e9e9e' }
          },
          labels: {
            style: { colors: '#9e9e9e' }
          }
        },
        grid: {
          borderColor: 'rgba(255, 255, 255, 0.1)',
          strokeDashArray: 4
        },
        theme: {
          mode: 'dark'
        },
        tooltip: {
          theme: 'dark',
          x: { 
            format: 'dd.MM HH:mm',
            formatter: function(val) {
              // Convert to local time
              const date = new Date(val);
              const hours = date.getHours().toString().padStart(2, '0');
              const minutes = date.getMinutes().toString().padStart(2, '0');
              const day = date.getDate().toString().padStart(2, '0');
              const month = (date.getMonth() + 1).toString().padStart(2, '0');
              return `${day}.${month} ${hours}:${minutes}`;
            }
          }
        },
        colors: ['#00E396']
      },
      chartSeries: [{
        name: 'PPH',
        data: []
      }]
    };
  },
  
  watch: {
    history: {
      immediate: true,
      deep: true,
      handler(newHistory) {
        this.updateChart(newHistory);
      }
    },
    stats: {
      immediate: true,
      deep: true,
      handler(newStats) {
        console.log('[PPHMonitor] Stats updated:', newStats);
        this.updateSpeedometer(newStats);
      }
    }
  },
  
  methods: {
    updateSpeedometer(stats) {
      if (!stats) return;
      
      // Dynamic max value - use the higher of current value or high value, with minimum of 100
      const dynamicMax = Math.max(100, stats.high * 1.2, stats.current * 1.5);
      
      // Convert current PPH to percentage based on dynamic max
      const percentage = Math.min((stats.current / dynamicMax) * 100, 100);
      
      // Update speedometer series
      this.speedometerSeries = [percentage];
      
      // Update color based on performance
      let colors = ['#00E396']; // Green by default
      if (stats.current < 30) {
        colors = ['#FF4560']; // Red for low PPH
      } else if (stats.current < 60) {
        colors = ['#FEB019']; // Orange for medium PPH
      }
      
      // Update the value formatter to show actual PPH value
      this.speedometerOptions = {
        ...this.speedometerOptions,
        colors: colors,
        plotOptions: {
          ...this.speedometerOptions.plotOptions,
          radialBar: {
            ...this.speedometerOptions.plotOptions.radialBar,
            dataLabels: {
              ...this.speedometerOptions.plotOptions.radialBar.dataLabels,
              value: {
                ...this.speedometerOptions.plotOptions.radialBar.dataLabels.value,
                formatter: function(val) {
                  // Return the actual current PPH value, not the percentage
                  return stats.current;
                }
              }
            }
          }
        }
      };
    },
    
    updateChart(history) {
      if (!history || !Array.isArray(history) || history.length === 0) {
        console.log('[PPHMonitor] No history data available');
        return;
      }
      
      console.log('[PPHMonitor] Updating chart with', history.length, 'data points');
      
      // Transform history data for ApexCharts
      // The timestamps should already be in ISO format from the backend
      const chartData = history.map(point => {
        const timestamp = new Date(point.x || point.timestamp);
        return {
          x: timestamp.getTime(),
          y: parseInt(point.y || point.value || 0)
        };
      });
      
      // Sort by timestamp to ensure correct order
      chartData.sort((a, b) => a.x - b.x);
      
      // Create new series array to trigger Vue reactivity
      this.chartSeries = [{
        name: 'PPH',
        data: chartData
      }];
    }
  }
};
</script>

<style scoped>
.section-title {
  font-size: 1.2rem;
  font-weight: 500;
}

.pph-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%);
}
</style>